import { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { Tabs } from "../../../components/TabSwitch/Tabs";
import AddRaiseETopUpRequest from "../../RaiseETopUpRequest/AddRaiseETopUpRequest";
import ETopUpList from "./ETopUpList";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

/**
 * ETopUpRequest Component
 * 
 * This component serves as the main container for E-Top Up request functionality.
 * It handles:
 * - Tab-based navigation between different request types
 * - Automatic redirection to default tab
 * - Conditional rendering of request forms and lists
 * - Breadcrumb navigation
 * 
 * The component supports two main tabs:
 * 1. E Top Up - For viewing and managing E-Top Up requests
 * 2. Digital Payment - For handling digital payment requests
 */
const ETopUpRequestRaised = () => {
  // Router hooks for navigation and URL parameters
  const { tab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const privilege = useSelector((state: RootState) => state.privileges.privileges);

  /**
   * Effect hook to handle default tab redirection
   * Redirects to the E-Top Up tab if no tab is specified in the URL
   */
  useEffect(() => {
    if (!tab) {
      navigate(`${location.pathname}/e-top-up`, { replace: true });
    }
  }, [tab, location.pathname, navigate]);

  /**
   * Handles tab change events
   * @param {string} newTab - The new tab to navigate to
   */
  const handleTabChange = (newTab: string) => {
    navigate(`/e-top-up-request/${newTab}`);
  };

  /**
   * Tab configuration array defining available tabs and their content
   * Each tab has:
   * - id: Unique identifier for the tab
   * - label: Display text for the tab
   * - content: Component to render when tab is active
   */
  const tabs = [
    {
      id: "e-top-up",
      label: "E Top Up",
      content: <></>,
    },
    {
      id: "digital-payment",
      label: "Digital Payment",
      content: <></>,
      isDisabled: true,
    },
  ];
  const addButtonClick = () => {
    navigate("/e-top-up-request/e-top-up/raise-e-top-up-request");
  };

  // Determine breadcrumb path and heading based on active tab
  const getBreadcrumbNavPath = () => {
    return ["E Top Up Request", "List of E Top Up Request Raised"];
  };

  const getHeading = () => {
    return "List of E Top Up Request Raised";
  };

  return (
    <div>
      {/* Breadcrumb navigation component */}
      <Breadcrumbs
        navPath={getBreadcrumbNavPath()}
        heading={getHeading()}
        noNavigation
        list={true}
        button={tab === "e-top-up" ? {
          buttonName: "Raise E Top Up Request",
          className: "w-[230px]",
          buttonOnclick: addButtonClick, // Corrected function type
        } : {}}
        hasAddPrivilege={privilege === "superadmin" ? false : true}
        // links={[ tab === 'pay' ? "/raise-e-top-up-request/pay" : "/raise-e-top-up-request/raise"]}
      />

      {/* Conditional rendering based on active tab */}
      {tab !== "e-top-up" ? (
        <AddRaiseETopUpRequest>
          <Tabs
            tabs={tabs}
            defaultTab="e-top-up"
            activeTab={tab}
            setActiveTab={handleTabChange}
          />
        </AddRaiseETopUpRequest>
      ) : (
        <ETopUpList>
          <Tabs
            tabs={tabs}
            defaultTab="raise"
            activeTab={tab}
            setActiveTab={handleTabChange}
          />
        </ETopUpList>
      )}
    </div>
  );
};

export default ETopUpRequestRaised;
